<template>
  <div :class="['flex flex-col mb-4', classes]">
    <q-select v-bind="$attrs" v-model="value">
      <template v-for="(_, slotName) in $slots" #[slotName]="slotProps">
        <slot :name="slotName" v-bind="slotProps || {}" />
      </template>
      <template #selected>
        <div :class="{ 'text-grey-6': !value }">
          {{ selectedLabel }}
        </div>
      </template>
    </q-select>
    <div v-if="isPresent(errorMessage)" class="mt-3 text-xs text-text-danger-tertiary">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, useAttrs } from 'vue';
import { useField } from 'vee-validate';
import { isBlank, isPresent } from '@/utils/lang';

interface Props {
  initialValue?: Record<string, any>;
  name: string;
  placeholder?: string;
  classes?: string[];
}

const props = withDefaults(defineProps<Props>(), {
  initialValue: () => ({}),
  classes: () => [],
  placeholder: '',
});

const { value, errorMessage } = useField<Record<string, any>>(props.name);
// Access passed attributes and compute the display label for the selected option
const attrs = useAttrs();
const selectedLabel = computed(() => {
  const opts = (attrs.options as Array<Record<string, any>>) || [];
  const valueKey = (attrs['option-value'] as string) || 'value';
  const labelKey = (attrs['option-label'] as string) || 'label';
  const rawValue: unknown = value.value;
  if (rawValue === undefined || rawValue === null || rawValue === '') {
    return props.placeholder;
  }
  const found = opts.find((opt) => opt[valueKey] === rawValue);
  return found ? found[labelKey] : String(rawValue);
});
if (isPresent(props.initialValue) && isBlank(value.value)) {
  value.value = props.initialValue;
}
</script>

<style lang="postcss" scoped>
@reference "tailwindcss";
:deep(.q-field) {
  .q-field__control {
    @apply min-h-[40px]
        before:!border-solid before:!border-[#d9d9d9];
  }

  .q-field__marginal {
    @apply h-[40px];
  }

  .q-field__native {
    @apply min-h-[40px] text-sm;
  }

  .q-field__label {
    @apply absolute
        top-[0]
        z-10
        -translate-y-1/2
        transform
        bg-[#fff]
        text-sm;
  }

  .q-field--float .q-field__label {
    @apply top-[35%] font-bold text-[#9A9A9F];
  }
}
</style>
